import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Row, Spinner } from 'react-bootstrap';
import { HorizontalBar } from 'react-chartjs-2';
import { MdEmail, MdLocationOn, MdLockOpen, MdRefresh, MdStorage } from 'react-icons/md';

import ApiGSenha from 'services/ApiGSenha';
import {
  chartColors,
  KPI,
  getDashboardCardSurface,
  getDashboardShellStyles,
  getChartTextColor
} from 'constants/dashboardColors';

const EMPTY_UNLOCKS_METRICS = {
  filters: {},
  totals: {
    total: 0,
    uniqueSerials: 0,
    uniqueEmails: 0,
    withCoordinates: 0,
    withAddress: 0
  },
  periods: {
    today: 0,
    last7Days: 0,
    last30Days: 0
  },
  timelines: {
    daily: [],
    monthly: [],
    byHour: [],
    byWeekDay: []
  },
  rankings: {},
  fieldCompleteness: {},
  geoPoints: [],
  recent: []
};

const rankingConfig = [
  ['serialnumber', 'Top Seriais'],
  ['email', 'Top E-mails'],
  ['equipamento', 'Top Equipamentos'],
  ['versaosw', 'Top Versões SW'],
  ['address', 'Top Endereços']
];

const normalizeUnlocksMetrics = (data = {}) => ({
  filters: data.filters || {},
  totals: {
    ...EMPTY_UNLOCKS_METRICS.totals,
    ...(data.totals || {})
  },
  periods: {
    ...EMPTY_UNLOCKS_METRICS.periods,
    ...(data.periods || {})
  },
  timelines: {
    ...EMPTY_UNLOCKS_METRICS.timelines,
    ...(data.timelines || {})
  },
  rankings: data.rankings || {},
  fieldCompleteness: data.fieldCompleteness || {},
  geoPoints: Array.isArray(data.geoPoints) ? data.geoPoints : [],
  recent: Array.isArray(data.recent) ? data.recent : []
});

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const formatDateTime = (value) => {
  if (!value) return '--';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';

  return date.toLocaleString('pt-BR');
};

const getSeriesLabel = (item, fallback) => {
  return item?.date || item?.day || item?.month || item?.hour || item?.weekDay || item?.label || item?.value || item?._id || fallback;
};

const getSeriesValue = (item) => Number(item?.count ?? item?.total ?? item?.value ?? 0);

const isAutoGeneratedLabel = (label) => /^Item\s+\d+$/i.test(String(label || '').trim());

const weekDayLabels = {
  0: 'Domingo',
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
  7: 'Domingo',
  sunday: 'Domingo',
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
  dom: 'Domingo',
  seg: 'Segunda-feira',
  ter: 'Terça-feira',
  qua: 'Quarta-feira',
  qui: 'Quinta-feira',
  sex: 'Sexta-feira',
  sab: 'Sábado',
  sábado: 'Sábado'
};

const formatWeekDayLabel = (label) => {
  const normalized = String(label || '').trim();
  const numeric = Number(normalized);

  if (!Number.isNaN(numeric) && weekDayLabels[numeric]) {
    return weekDayLabels[numeric];
  }

  return weekDayLabels[normalized.toLowerCase()] || normalized;
};

const normalizeSeries = (source = [], labelFormatter = (label) => label) => {
  if (Array.isArray(source)) {
    return source
      .map((item, index) => ({
        label: labelFormatter(getSeriesLabel(item, `Item ${index + 1}`)),
        value: getSeriesValue(item)
      }))
      .filter((item) => item.value > 0 && !isAutoGeneratedLabel(item.label));
  }

  return Object.entries(source || {})
    .map(([label, value]) => ({
      label: labelFormatter(label),
      value: Number(value || 0)
    }))
    .filter((item) => item.value > 0 && !isAutoGeneratedLabel(item.label));
};

const normalizeRanking = (source = []) => {
  return normalizeSeries(source)
    .map((item, index) => {
      const raw = Array.isArray(source) ? source[index] : null;

      return {
        ...item,
        lastUnlockAt: raw?.lastUnlockAt
      };
    })
    .slice(0, 8);
};

const createGradient = (ctx, colors, darkMode) => {
  const gradient = ctx.createLinearGradient(0, 0, 300, 0);
  gradient.addColorStop(0, darkMode ? colors.dark : colors.light);
  gradient.addColorStop(1, darkMode ? `${colors.dark}99` : `${colors.light}99`);
  return gradient;
};

const getChartOptions = (darkMode) => ({
  animation: {
    onComplete: function () {
      const chart = this.chart;
      const ctx = chart.ctx;

      ctx.save();
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      chart.config.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.controller.getDatasetMeta(datasetIndex);
        const drawnLabels = [];
        const chartRight = chart.chartArea?.right || chart.width;
        const chartLeft = chart.chartArea?.left || 0;

        meta.data.forEach((bar, index) => {
          const value = formatNumber(dataset.data[index]);
          const model = bar._model;
          const barWidth = Math.abs(model.x - model.base);
          const textWidth = ctx.measureText(value).width;
          const fitsInside = barWidth > textWidth + 18;
          const x = fitsInside
            ? Math.min(Math.max(model.x - 10, chartLeft + textWidth + 4), chartRight - 4)
            : Math.min(model.x + textWidth + 10, chartRight - 4);
          const labelBox = {
            x1: x - textWidth - 4,
            x2: x + 4,
            y1: model.y - 7,
            y2: model.y + 7
          };
          const overlaps = drawnLabels.some((box) => (
            labelBox.x1 < box.x2 &&
            labelBox.x2 > box.x1 &&
            labelBox.y1 < box.y2 &&
            labelBox.y2 > box.y1
          ));

          if (overlaps) return;

          ctx.fillStyle = fitsInside ? '#ffffff' : darkMode ? '#f8f9fa' : '#1c4220';
          ctx.fillText(value, x, model.y);
          drawnLabels.push(labelBox);
        });
      });

      ctx.restore();
    }
  },
  scales: {
    yAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(darkMode),
        fontStyle: 'bold',
        precision: 0
      },
      gridLines: {
        display: false
      },
      maxBarThickness: 30
    }],
    xAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(darkMode),
        precision: 0
      },
      gridLines: {
        color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        zeroLineColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    }]
  },
  legend: {
    display: false
  },
  maintainAspectRatio: false,
  responsive: true,
  tooltips: {
    backgroundColor: darkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
    titleFontColor: darkMode ? '#f8f9fa' : '#333',
    bodyFontColor: darkMode ? '#adb5bd' : '#666',
    callbacks: {
      label: (item) => `Quantidade: ${item.value}`
    }
  }
});

const MetricCard = ({ icon, title, value, subtitle, accent, darkMode }) => (
  <Col xs={12} sm={6} lg={3}>
    <div
      className="h-100"
      style={{
        borderRadius: '18px',
        padding: '18px',
        ...getDashboardCardSurface(),
        borderTop: `4px solid ${accent}`
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p className="text-uppercase mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', color: darkMode ? '#adb5bd' : '#6c757d' }}>
            {title}
          </p>
          <h2 className="mb-0 fw-bold" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>{formatNumber(value)}</h2>
        </div>
        <div className="d-inline-flex align-items-center justify-content-center" style={{ width: '46px', height: '46px', borderRadius: '14px', background: `${accent}22`, color: accent }}>
          {icon}
        </div>
      </div>
      {subtitle && <small style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>{subtitle}</small>}
    </div>
  </Col>
);

const ChartCard = ({ title, source, colorKey, darkMode, lg = 6, height = 300, labelFormatter }) => {
  const series = normalizeSeries(source, labelFormatter);
  const colors = chartColors[colorKey] || chartColors.green;
  const options = getChartOptions(darkMode);

  return (
    <Col xs={12} lg={lg}>
      <div
        className="h-100"
        style={{
          padding: '18px',
          borderRadius: '18px',
          ...getDashboardCardSurface(),
          borderTop: `4px solid ${darkMode ? colors.dark : colors.light}`
        }}
      >
        <h5 className="fw-bold mb-3" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>{title}</h5>
        <div style={{ height, position: 'relative' }}>
          {series.length ? (
            <HorizontalBar
              data={(canvas) => {
                const ctx = canvas.getContext('2d');
                return {
                  labels: series.map((item) => item.label),
                  datasets: [{
                    label: 'Desbloqueios',
                    data: series.map((item) => item.value),
                    backgroundColor: createGradient(ctx, colors, darkMode),
                    borderColor: darkMode ? colors.dark : colors.light,
                    borderWidth: 1
                  }]
                };
              }}
              options={options}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">Sem dados para exibir</div>
          )}
        </div>
      </div>
    </Col>
  );
};

const RankingCard = ({ title, items, darkMode, accent = '#8fad15' }) => {
  const ranking = normalizeRanking(items);
  const maxValue = Math.max(...ranking.map((item) => item.value), 0);

  return (
    <Col xs={12} md={6} xl={4}>
      <div className="h-100" style={{ padding: '18px', borderRadius: '18px', ...getDashboardCardSurface() }}>
        <h5 className="fw-bold mb-3" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>{title}</h5>
        {ranking.length ? ranking.map((item) => (
          <div key={`${title}-${item.label}`} className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-truncate me-2" title={item.label} style={{ color: darkMode ? '#f8f9fa' : '#1c4220', fontWeight: 600 }}>{item.label || 'Não informado'}</span>
              <span style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>{formatNumber(item.value)}</span>
            </div>
            <div style={{ height: '8px', background: darkMode ? 'rgba(255, 255, 255, 0.08)' : '#edf2ee', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ width: `${maxValue ? Math.round((item.value / maxValue) * 100) : 0}%`, height: '100%', background: accent, borderRadius: '999px' }} />
            </div>
            {item.lastUnlockAt && <small style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>Último: {formatDateTime(item.lastUnlockAt)}</small>}
          </div>
        )) : (
          <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '180px' }}>Sem dados para exibir</div>
        )}
      </div>
    </Col>
  );
};

const UnlocksDashboard = ({ darkMode, onLoadingChange }) => {
  const [metrics, setMetrics] = useState(EMPTY_UNLOCKS_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (onLoadingChange) onLoadingChange(loading);
  }, [loading, onLoadingChange]);
  const styles = getDashboardShellStyles(darkMode);
  const { totals, periods, timelines, rankings } = metrics;

  const fetchUnlocksMetrics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await ApiGSenha.get('/log/dashboard/unblocks');
      setMetrics(normalizeUnlocksMetrics(data));
    } catch (err) {
      setMetrics(EMPTY_UNLOCKS_METRICS);
      setError('Não foi possível carregar o dashboard de desbloqueios.');
      console.log('Erro ao buscar dashboard de desbloqueios:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnlocksMetrics();
  }, [fetchUnlocksMetrics]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5" style={styles.panel}>
        <Spinner animation="border" style={{ color: '#8fad15' }} />
        <span className="ms-2" style={styles.mutedText}>Carregando dashboard de desbloqueios...</span>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4" style={{ ...styles.panel, padding: '20px' }}>
        <div>
          <Badge className="mb-2" style={{ background: '#8fad15', color: '#fff' }}>Painel Executivo</Badge>
          <h3 className="fw-bold mb-1" style={styles.titleText}>Dashboard de Ativações</h3>
          <p className="mb-0" style={styles.mutedText}>
            Visão geral de desbloqueios, localizações, períodos recentes e rankings operacionais.
          </p>
        </div>
        <Button variant="outline-success" onClick={fetchUnlocksMetrics}>
          <MdRefresh className="me-1" /> Atualizar
        </Button>
      </div>

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {!error && !totals.total ? (
        <div style={{ ...styles.panel, padding: '28px' }}>
          <h4 className="fw-bold mb-2" style={styles.titleText}>Nenhum desbloqueio encontrado</h4>
          <p className="mb-0" style={styles.mutedText}>A API retornou total igual a zero para os filtros informados.</p>
        </div>
      ) : !error && (
        <>
          <Row className="g-3 mb-3">
            <MetricCard icon={<MdLockOpen size={28} />} title="Desbloqueios" value={totals.total} subtitle="Total no período" accent={KPI.primary} darkMode={darkMode} />
            <MetricCard icon={<MdStorage size={28} />} title="Seriais Únicos" value={totals.uniqueSerials} subtitle="Equipamentos distintos" accent={KPI.secondary} darkMode={darkMode} />
            <MetricCard icon={<MdEmail size={28} />} title="E-mails Únicos" value={totals.uniqueEmails} subtitle="Usuários distintos" accent={KPI.tertiary} darkMode={darkMode} />
            <MetricCard icon={<MdLocationOn size={28} />} title="Com Coordenadas" value={totals.withCoordinates} subtitle="Coordenadas informadas" accent={KPI.quaternary} darkMode={darkMode} />
          </Row>

          <Row className="g-3 mb-4">
            <MetricCard icon={<MdLocationOn size={28} />} title="Com Endereço" value={totals.withAddress} subtitle="Endereço informado" accent={KPI.secondary} darkMode={darkMode} />
            <MetricCard icon={<MdLockOpen size={28} />} title="Hoje" value={periods.today} subtitle="Desbloqueios no dia" accent={KPI.primary} darkMode={darkMode} />
            <MetricCard icon={<MdLockOpen size={28} />} title="Últimos 7 Dias" value={periods.last7Days} subtitle="Janela semanal" accent={KPI.tertiary} darkMode={darkMode} />
            <MetricCard icon={<MdLockOpen size={28} />} title="Últimos 30 Dias" value={periods.last30Days} subtitle="Janela mensal" accent={KPI.quaternary} darkMode={darkMode} />
          </Row>

          <Row className="g-4 mb-4">
            <ChartCard title="Desbloqueios por Dia" source={timelines.daily} colorKey="green" darkMode={darkMode} lg={6} height={320} />
            <ChartCard title="Desbloqueios por Mês" source={timelines.monthly} colorKey="blue" darkMode={darkMode} lg={6} height={320} />
            <ChartCard title="Por Hora do Dia" source={timelines.byHour} colorKey="teal" darkMode={darkMode} lg={6} height={300} />
            <ChartCard title="Por Dia da Semana" source={timelines.byWeekDay} colorKey="purple" darkMode={darkMode} lg={6} height={300} labelFormatter={formatWeekDayLabel} />
          </Row>

          <Row className="g-4 mb-4">
            {rankingConfig.map(([key, title]) => (
              <RankingCard key={key} title={title} items={rankings[key]} darkMode={darkMode} accent={KPI.secondary} />
            ))}
          </Row>

        </>
      )}
    </>
  );
};

export default UnlocksDashboard;
